import AddIcon from "@mui/icons-material/Add";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router";

import {
  useCreateAdminV2AssistantConversationMutation,
  useDeleteAdminV2AssistantConversationMutation,
  useGetAdminV2AssistantConversationsQuery,
  useGetAdminV2AssistantMessagesQuery,
  useGetAdminV2AssistantSuggestionsQuery,
  useSendAdminV2AssistantMessageMutation,
} from "@/store/api/baseApi";
import type {
  AdminV2AssistantConversation,
  AdminV2AssistantEvidence,
  AdminV2AssistantMessage,
} from "@/types/adminV2";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { normalizeAdminFrontendPath } from "@/utils/navigation/normalizeAdminFrontendPath";

const ASSISTANT_OPEN_STORAGE_KEY = "hc-admin-ai-assistant-open";
const ASSISTANT_CONVERSATION_STORAGE_KEY = "hc-admin-ai-assistant-conversation";

const withUserScope = (baseKey: string, userId: string | undefined): string =>
  userId ? `${baseKey}:${userId}` : baseKey;

const readStoredBoolean = (key: string, fallback: boolean): boolean => {
  const raw = window.localStorage.getItem(key);
  if (raw === "true") {
    return true;
  }
  if (raw === "false") {
    return false;
  }
  return fallback;
};

const formatConversationLabel = (
  conversation: AdminV2AssistantConversation,
): string => {
  const fallback = `Chat ${new Date(conversation.createdAt).toLocaleDateString()}`;
  const trimmed = conversation.title?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
};

const formatEvidencePath = (evidence: AdminV2AssistantEvidence): string => {
  if (
    typeof evidence.startLine === "number" &&
    Number.isFinite(evidence.startLine) &&
    typeof evidence.endLine === "number" &&
    Number.isFinite(evidence.endLine)
  ) {
    return `${evidence.path}:${evidence.startLine}-${evidence.endLine}`;
  }

  if (
    typeof evidence.startLine === "number" &&
    Number.isFinite(evidence.startLine)
  ) {
    return `${evidence.path}:${evidence.startLine}`;
  }

  return evidence.path;
};

const normalizeAdminDeepLink = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed.startsWith("/") || trimmed.includes("://")) {
    return null;
  }

  const normalized = normalizeAdminFrontendPath(trimmed.replace(/\s+/g, ""));
  if (!normalized) {
    return null;
  }
  if (normalized.includes("..")) {
    return null;
  }

  if (!/^\/[A-Za-z0-9/_?&=:%.-]*$/.test(normalized)) {
    return null;
  }

  return normalized;
};

const parseDeepLinksFromContent = (content: string): string[] => {
  const lines = content.split("\n");
  const links: string[] = [];
  let inDeepLinksSection = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!inDeepLinksSection && /^deep links:/i.test(trimmed)) {
      inDeepLinksSection = true;
      continue;
    }
    if (!inDeepLinksSection) {
      continue;
    }
    if (!trimmed) {
      break;
    }
    if (!trimmed.startsWith("-")) {
      break;
    }

    const candidate = normalizeAdminDeepLink(trimmed.replace(/^-+\s*/, ""));
    if (candidate) {
      links.push(candidate);
    }
  }

  return Array.from(new Set(links));
};

const parseInlineAdminPathsFromContent = (content: string): string[] => {
  const matches = content.match(/(?:^|[\s(])\/[A-Za-z0-9/_?&=:%.-]+/g) ?? [];
  const links: string[] = [];

  for (const match of matches) {
    const candidate = match.trim().replace(/^[\s(]+/, "");
    const normalized = normalizeAdminDeepLink(candidate);
    if (normalized) {
      links.push(normalized);
    }
  }

  return Array.from(new Set(links));
};

const stripDeepLinksSection = (content: string): string => {
  const lines = content.split("\n");
  const kept: string[] = [];
  let inDeepLinksSection = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!inDeepLinksSection && /^deep links:/i.test(trimmed)) {
      inDeepLinksSection = true;
      continue;
    }

    if (!inDeepLinksSection) {
      kept.push(line);
      continue;
    }

    if (!trimmed) {
      inDeepLinksSection = false;
      continue;
    }

    if (trimmed.startsWith("-")) {
      continue;
    }

    inDeepLinksSection = false;
    kept.push(line);
  }

  return kept
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const extractMessageDeepLinks = (
  message: AdminV2AssistantMessage,
): string[] => {
  const linksFromMetadata: string[] = [];
  if (
    message.metadata &&
    typeof message.metadata === "object" &&
    !Array.isArray(message.metadata)
  ) {
    const metadata = message.metadata as Record<string, unknown>;
    if (Array.isArray(metadata.deepLinks)) {
      for (const value of metadata.deepLinks) {
        if (typeof value !== "string") {
          continue;
        }
        const normalized = normalizeAdminDeepLink(value);
        if (normalized) {
          linksFromMetadata.push(normalized);
        }
      }
    }
  }

  const fallbackSectionLinks = parseDeepLinksFromContent(message.content);
  const inlineLinks = parseInlineAdminPathsFromContent(message.content);
  return Array.from(
    new Set([...linksFromMetadata, ...fallbackSectionLinks, ...inlineLinks]),
  );
};

const buildTranscriptPlainText = (input: {
  conversationLabel: string;
  messages: AdminV2AssistantMessage[];
}): string => {
  const lines: string[] = [
    `Chat: ${input.conversationLabel}`,
    `Exported: ${new Date().toISOString()}`,
    "",
  ];

  for (const message of input.messages) {
    const roleLabel = message.role === "assistant" ? "Assistant" : "User";
    lines.push(`${roleLabel}:`);
    lines.push(message.content);

    if (message.role === "assistant" && message.evidence?.length) {
      lines.push("");
      lines.push("Evidence:");
      for (const evidence of message.evidence) {
        lines.push(`- ${formatEvidencePath(evidence)}`);
      }
    }

    lines.push("");
  }

  return lines.join("\n").trim();
};

const isNotFoundError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }

  const candidate = error as {
    status?: unknown;
    originalStatus?: unknown;
  };

  return candidate.status === 404 || candidate.originalStatus === 404;
};

const getErrorStatus = (error: unknown): number | null => {
  if (!error || typeof error !== "object") {
    return null;
  }

  const candidate = error as {
    status?: unknown;
    originalStatus?: unknown;
  };

  if (typeof candidate.status === "number") {
    return candidate.status;
  }
  if (typeof candidate.originalStatus === "number") {
    return candidate.originalStatus;
  }

  return null;
};

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "AdminAiAssistantWidget",
);

const copyTextToClipboard = async (value: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(value);
    return;
  } catch {
    // Fallback for environments where clipboard API exists but is blocked.
  }

  const element = document.createElement("textarea");
  element.value = value;
  element.style.position = "fixed";
  element.style.opacity = "0";
  document.body.appendChild(element);
  element.focus();
  element.select();
  document.execCommand("copy");
  document.body.removeChild(element);
};

interface AdminAiAssistantWidgetProps {
  currentUserId?: string;
  /**
   * Nav PRD §8: rendered inside the shell's AI Assistant side panel. The
   * panel owns opening, closing and the title; the chat is unchanged.
   */
  hosted?: { isOpen?: boolean; onClose: () => void };
}

export const AdminAiAssistantWidget: React.FC<AdminAiAssistantWidgetProps> = ({
  currentUserId,
  hosted,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const isHosted = Boolean(hosted);
  const [standaloneOpen, setIsOpen] = useState(false);
  const isOpen = hosted ? (hosted.isOpen ?? true) : standaloneOpen;
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  const [messageDraft, setMessageDraft] = useState("");
  const [uiError, setUiError] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [transcriptCopied, setTranscriptCopied] = useState(false);
  const [isStartingNewChat, setIsStartingNewChat] = useState(false);
  const [expandedEvidenceMessageIds, setExpandedEvidenceMessageIds] = useState<
    Record<string, boolean>
  >({});

  const threadBottomRef = useRef<HTMLDivElement | null>(null);
  const previousAssistantDisabledRef = useRef(false);
  const copiedMessageTimeoutRef = useRef<number | null>(null);
  const transcriptCopiedTimeoutRef = useRef<number | null>(null);
  const assistantQueryEnabled = isOpen;

  // The copied-indicator timers must not outlive the component: a timer that
  // fires after unmount calls setState on a dead component and crashes
  // teardown environments where window is already gone.
  useEffect(
    () => () => {
      if (copiedMessageTimeoutRef.current !== null) {
        window.clearTimeout(copiedMessageTimeoutRef.current);
      }
      if (transcriptCopiedTimeoutRef.current !== null) {
        window.clearTimeout(transcriptCopiedTimeoutRef.current);
      }
    },
    [],
  );

  const {
    data: suggestionsData,
    error: suggestionsError,
    isFetching: isSuggestionsFetching,
  } = useGetAdminV2AssistantSuggestionsQuery(
    assistantQueryEnabled ? undefined : skipToken,
  );

  const {
    data: conversationsData,
    error: conversationsError,
    isFetching: isConversationsFetching,
    refetch: refetchConversations,
  } = useGetAdminV2AssistantConversationsQuery(
    assistantQueryEnabled
      ? {
          limit: 20,
        }
      : skipToken,
  );

  const [createConversation, createConversationState] =
    useCreateAdminV2AssistantConversationMutation();
  const [sendMessage, sendMessageState] =
    useSendAdminV2AssistantMessageMutation();
  const [deleteConversation, deleteConversationState] =
    useDeleteAdminV2AssistantConversationMutation();

  const messagesQueryArg = useMemo(() => {
    if (!assistantQueryEnabled || !activeConversationId) {
      return skipToken;
    }

    return {
      conversationId: activeConversationId,
      limit: 200,
    };
  }, [activeConversationId, assistantQueryEnabled]);

  const {
    data: messagesData,
    isFetching: isMessagesFetching,
    refetch: refetchMessages,
  } = useGetAdminV2AssistantMessagesQuery(messagesQueryArg);

  const conversations = useMemo(
    () => conversationsData?.conversations ?? [],
    [conversationsData],
  );
  const suggestions = suggestionsData?.suggestions ?? [];
  const messages = useMemo(
    () => (activeConversationId ? (messagesData?.messages ?? []) : []),
    [activeConversationId, messagesData],
  );
  const activeConversation = useMemo(
    () =>
      activeConversationId
        ? (conversations.find(
            (conversation) => conversation.id === activeConversationId,
          ) ?? null)
        : null,
    [activeConversationId, conversations],
  );
  const recentConversations = useMemo(
    () =>
      conversations
        .filter((conversation) => conversation.id !== activeConversationId)
        .slice(0, 5),
    [activeConversationId, conversations],
  );

  const isAssistantDisabled =
    isNotFoundError(conversationsError) || isNotFoundError(suggestionsError);
  const conversationsErrorStatus = getErrorStatus(conversationsError);
  const suggestionsErrorStatus = getErrorStatus(suggestionsError);
  const isNewChatTransitioning =
    isStartingNewChat || createConversationState.isLoading;

  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    const openKey = withUserScope(ASSISTANT_OPEN_STORAGE_KEY, currentUserId);
    const conversationKey = withUserScope(
      ASSISTANT_CONVERSATION_STORAGE_KEY,
      currentUserId,
    );

    // Hosted, the side panel decides whether the assistant is open.
    if (!isHosted) setIsOpen(readStoredBoolean(openKey, false));
    setActiveConversationId(window.localStorage.getItem(conversationKey));
  }, [currentUserId, isHosted]);

  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    if (isHosted) return;
    const openKey = withUserScope(ASSISTANT_OPEN_STORAGE_KEY, currentUserId);
    window.localStorage.setItem(openKey, String(isOpen));
  }, [currentUserId, isHosted, isOpen]);

  useEffect(() => {
    if (!currentUserId) {
      return;
    }

    const conversationKey = withUserScope(
      ASSISTANT_CONVERSATION_STORAGE_KEY,
      currentUserId,
    );

    if (!activeConversationId) {
      window.localStorage.removeItem(conversationKey);
      return;
    }

    window.localStorage.setItem(conversationKey, activeConversationId);
  }, [activeConversationId, currentUserId]);

  useEffect(() => {
    if (conversations.length === 0) {
      setActiveConversationId(null);
      return;
    }

    if (isNewChatTransitioning) {
      return;
    }

    if (
      activeConversationId &&
      conversations.some(
        (conversation) => conversation.id === activeConversationId,
      )
    ) {
      return;
    }

    setActiveConversationId(conversations[0]?.id ?? null);
  }, [activeConversationId, conversations, isNewChatTransitioning]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const scrollTarget = threadBottomRef.current;
    if (!scrollTarget || typeof scrollTarget.scrollIntoView !== "function") {
      return;
    }

    scrollTarget.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [isOpen, messages]);

  useEffect(() => {
    const wasDisabled = previousAssistantDisabledRef.current;
    if (isAssistantDisabled && !wasDisabled) {
      logger.warn("Admin AI assistant API unavailable", {
        routePath: location.pathname,
        conversationsErrorStatus,
        suggestionsErrorStatus,
      });
    }

    if (!isAssistantDisabled && wasDisabled) {
      logger.info("Admin AI assistant API recovered", {
        routePath: location.pathname,
      });
    }

    previousAssistantDisabledRef.current = isAssistantDisabled;
  }, [
    conversationsErrorStatus,
    isAssistantDisabled,
    location.pathname,
    suggestionsErrorStatus,
  ]);

  const ensureConversationId = async (): Promise<string | null> => {
    if (activeConversationId) {
      return activeConversationId;
    }

    const created = await createConversation({}).unwrap();
    setActiveConversationId(created.conversation.id);
    await refetchConversations();
    return created.conversation.id;
  };

  const handleSendMessage = async (rawInput?: string) => {
    if (isAssistantDisabled) {
      setUiError("Assistant is unavailable right now. Please retry shortly.");
      return;
    }

    if (isNewChatTransitioning) {
      return;
    }

    const nextMessage = (rawInput ?? messageDraft).trim();
    if (!nextMessage) {
      return;
    }

    setUiError(null);

    try {
      const conversationId = await ensureConversationId();
      if (!conversationId) {
        setUiError("Unable to create a chat right now.");
        return;
      }

      await sendMessage({
        conversationId,
        message: nextMessage,
        currentPath: location.pathname,
      }).unwrap();

      setMessageDraft("");
      await Promise.all([refetchConversations(), refetchMessages()]);
    } catch {
      setUiError("I could not send your message right now. Please retry.");
    }
  };

  const handleCreateNewChat = async () => {
    if (isAssistantDisabled) {
      setUiError("Assistant is unavailable right now. Please retry shortly.");
      return;
    }

    setUiError(null);
    setIsStartingNewChat(true);
    setActiveConversationId(null);
    setMessageDraft("");

    try {
      const created = await createConversation({}).unwrap();
      setActiveConversationId(created.conversation.id);
      await Promise.all([refetchConversations(), refetchMessages()]);
    } catch {
      setUiError("I could not create a new chat right now.");
    } finally {
      setIsStartingNewChat(false);
    }
  };

  const handleDeleteCurrentChat = async () => {
    if (isAssistantDisabled) {
      setUiError("Assistant is unavailable right now. Please retry shortly.");
      return;
    }

    if (!activeConversationId) {
      return;
    }

    const confirmed = window.confirm(
      "Delete this chat? This cannot be undone.",
    );
    if (!confirmed) {
      return;
    }

    setUiError(null);

    try {
      await deleteConversation({
        conversationId: activeConversationId,
      }).unwrap();

      setActiveConversationId(null);
      setMessageDraft("");
      await refetchConversations();
    } catch {
      setUiError("I could not delete this chat right now.");
    }
  };

  const handleCopyAnswer = async (message: AdminV2AssistantMessage) => {
    try {
      await copyTextToClipboard(message.content);
      setUiError(null);
      setCopiedMessageId(message.id);
      if (copiedMessageTimeoutRef.current !== null) {
        window.clearTimeout(copiedMessageTimeoutRef.current);
      }
      copiedMessageTimeoutRef.current = window.setTimeout(() => {
        copiedMessageTimeoutRef.current = null;
        setCopiedMessageId((previous) =>
          previous === message.id ? null : previous,
        );
      }, 1500);
    } catch {
      setUiError("Could not copy that answer.");
    }
  };

  const toggleEvidenceVisibility = (messageId: string) => {
    setExpandedEvidenceMessageIds((previous) => ({
      ...previous,
      [messageId]: !previous[messageId],
    }));
  };

  const handleCopyTranscript = async () => {
    if (!activeConversation || messages.length === 0) {
      setUiError("No chat transcript is available to copy.");
      return;
    }

    try {
      const transcript = buildTranscriptPlainText({
        conversationLabel: formatConversationLabel(activeConversation),
        messages,
      });

      await copyTextToClipboard(transcript);
      setUiError(null);
      setTranscriptCopied(true);
      if (transcriptCopiedTimeoutRef.current !== null) {
        window.clearTimeout(transcriptCopiedTimeoutRef.current);
      }
      transcriptCopiedTimeoutRef.current = window.setTimeout(() => {
        transcriptCopiedTimeoutRef.current = null;
        setTranscriptCopied(false);
      }, 1500);
    } catch {
      setUiError("Could not copy this chat transcript.");
    }
  };

  if (!isOpen) {
    if (hosted) return null;
    return (
      <Box
        data-hc-admin-ai-assistant-root
        sx={{
          position: "fixed",
          right: { xs: 12, md: 24 },
          bottom: { xs: 12, md: 24 },
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<ChatBubbleOutlineIcon />}
          onClick={() => setIsOpen(true)}
          aria-label="Open AI assistant"
        >
          AI Assistant
        </Button>
      </Box>
    );
  }

  return (
    <Paper
      data-hc-admin-ai-assistant-root
      elevation={hosted ? 0 : 8}
      sx={{
        position: "fixed",
        right: { xs: 12, md: 24 },
        bottom: { xs: 12, md: 24 },
        zIndex: (theme) => theme.zIndex.modal + 1,
        width: isMobile ? "calc(100vw - 24px)" : 520,
        height: isMobile ? "82vh" : 880,
        maxHeight: isMobile ? "calc(100vh - 24px)" : "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
        ...(hosted
          ? {
              position: "relative",
              // Replace (not just inset) the standalone responsive offsets:
              // relative right/bottom would shift the capture inside the panel.
              top: "auto",
              right: "auto",
              bottom: "auto",
              left: "auto",
              width: "100%",
              height: "100%",
              maxHeight: "none",
              borderRadius: 0,
              boxShadow: "none",
              zIndex: "auto",
            }
          : {}),
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: "background.default",
          gap: 1,
          position: "relative",
        }}
      >
        {hosted ? (
          <span />
        ) : (
          <Stack spacing={0} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Admin AI Assistant
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Internal docs + code answers
            </Typography>
          </Stack>
        )}

        <Tooltip
          title={
            transcriptCopied
              ? "Transcript copied"
              : "Copy current chat transcript"
          }
        >
          <span
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <IconButton
              size="small"
              aria-label="Copy transcript"
              onClick={() => {
                void handleCopyTranscript();
              }}
              disabled={isAssistantDisabled || messages.length === 0}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Stack direction="row" spacing={0.5}>
          <Tooltip title="New chat">
            <span>
              <IconButton
                size="small"
                aria-label="New chat"
                onClick={handleCreateNewChat}
                disabled={isAssistantDisabled || isNewChatTransitioning}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete chat">
            <span>
              <IconButton
                size="small"
                aria-label="Delete chat"
                onClick={handleDeleteCurrentChat}
                disabled={
                  isAssistantDisabled ||
                  !activeConversationId ||
                  deleteConversationState.isLoading
                }
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {hosted ? null : (
            <Tooltip title="Close">
              <IconButton
                size="small"
                aria-label="Close assistant"
                onClick={() => setIsOpen(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
        <TextField
          select
          size="small"
          fullWidth
          value={activeConversationId ?? ""}
          onChange={(event) => setActiveConversationId(event.target.value)}
          label="Chat"
          disabled={
            isAssistantDisabled ||
            isConversationsFetching ||
            conversations.length === 0
          }
        >
          {conversations.map((conversation) => (
            <MenuItem key={conversation.id} value={conversation.id}>
              {formatConversationLabel(conversation)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.75,
        }}
      >
        {isSuggestionsFetching ? (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
            }}
          >
            Loading suggestions...
          </Typography>
        ) : null}

        {isAssistantDisabled ? (
          <Alert severity="warning" sx={{ width: "100%" }}>
            Assistant API is unavailable right now. Check backend logs and
            retry.
          </Alert>
        ) : null}

        {suggestions.slice(0, 4).map((question) => (
          <Chip
            key={question}
            size="small"
            label={question}
            variant="outlined"
            disabled={isAssistantDisabled || isNewChatTransitioning}
            onClick={() => {
              void handleSendMessage(question);
            }}
          />
        ))}
      </Box>

      <Divider />

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 1.75,
          py: 1.25,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          opacity: isStartingNewChat ? 0.55 : 1,
          transition: "opacity 140ms ease-out",
        }}
      >
        {uiError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {uiError}
          </Alert>
        ) : null}

        {isStartingNewChat ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            Starting a new chat...
          </Alert>
        ) : null}

        {!activeConversationId && !isConversationsFetching ? (
          <Alert severity="info">
            Start a new chat, or pick a suggestion to begin.
          </Alert>
        ) : null}

        {isMessagesFetching ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 2,
            }}
          >
            <CircularProgress size={18} />
          </Box>
        ) : null}

        {messages.map((message) => {
          const isAssistant = message.role === "assistant";
          const deepLinks = isAssistant ? extractMessageDeepLinks(message) : [];
          const renderedContent = isAssistant
            ? stripDeepLinksSection(message.content)
            : message.content;

          return (
            <Box
              key={message.id}
              sx={{
                alignSelf: isAssistant ? "flex-start" : "flex-end",
                maxWidth: "94%",
                borderRadius: 1.5,
                px: 1.75,
                py: 1.35,
                bgcolor: isAssistant ? "background.default" : "primary.main",
                color: isAssistant ? "text.primary" : "primary.contrastText",
                border: isAssistant
                  ? (theme) => `1px solid ${theme.palette.divider}`
                  : "none",
              }}
            >
              <Stack spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.45,
                  }}
                >
                  {renderedContent}
                </Typography>

                {isAssistant && deepLinks.length > 0 ? (
                  <Stack spacing={0.25}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Links
                    </Typography>
                    {deepLinks.map((deepLink) => (
                      <Link
                        key={`${message.id}-${deepLink}`}
                        component={RouterLink}
                        to={deepLink}
                        variant="caption"
                        underline="hover"
                        color="primary"
                        sx={{ wordBreak: "break-all" }}
                      >
                        {deepLink}
                      </Link>
                    ))}
                  </Stack>
                ) : null}

                {isAssistant && message.evidence?.length ? (
                  <Stack spacing={0.5}>
                    <Button
                      variant="text"
                      size="small"
                      sx={{
                        alignSelf: "flex-start",
                        px: 0,
                        minWidth: 0,
                        textTransform: "none",
                        fontSize: "0.75rem",
                      }}
                      aria-label={
                        expandedEvidenceMessageIds[message.id]
                          ? "Hide references"
                          : "Show references"
                      }
                      onClick={() => {
                        toggleEvidenceVisibility(message.id);
                      }}
                    >
                      {expandedEvidenceMessageIds[message.id]
                        ? `Hide references (${message.evidence.length})`
                        : `References (${message.evidence.length})`}
                    </Button>

                    <Collapse
                      in={Boolean(expandedEvidenceMessageIds[message.id])}
                    >
                      <Stack spacing={0.25}>
                        {message.evidence.map((evidence, index) => (
                          <Typography
                            key={`${message.id}-evidence-${index}`}
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              wordBreak: "break-all",
                            }}
                          >
                            {formatEvidencePath(evidence)}
                          </Typography>
                        ))}
                      </Stack>
                    </Collapse>
                  </Stack>
                ) : null}

                {isAssistant ? (
                  <Box>
                    <Button
                      size="small"
                      variant="text"
                      sx={{
                        fontSize: "0.72rem",
                        lineHeight: 1.2,
                      }}
                      startIcon={<ContentCopyIcon />}
                      onClick={() => {
                        void handleCopyAnswer(message);
                      }}
                    >
                      {copiedMessageId === message.id
                        ? "Copied"
                        : "Copy answer"}
                    </Button>
                  </Box>
                ) : null}
              </Stack>
            </Box>
          );
        })}

        {sendMessageState.isLoading && activeConversationId ? (
          <Box
            sx={{
              alignSelf: "flex-start",
              maxWidth: "72%",
              borderRadius: 1.5,
              px: 1.5,
              py: 1.1,
              bgcolor: "background.default",
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
            aria-live="polite"
            aria-label="Assistant is thinking"
          >
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <CircularProgress size={14} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Thinking...
              </Typography>
            </Stack>
          </Box>
        ) : null}

        <div ref={threadBottomRef} />
      </Box>

      <Divider />

      {recentConversations.length > 0 ? (
        <>
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              backgroundColor: "background.default",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
              }}
            >
              Recent chats
            </Typography>
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              sx={{ flexWrap: "wrap" }}
            >
              {recentConversations.map((conversation) => (
                <Chip
                  key={`history-${conversation.id}`}
                  size="small"
                  variant="outlined"
                  label={formatConversationLabel(conversation)}
                  onClick={() => {
                    setActiveConversationId(conversation.id);
                    setUiError(null);
                  }}
                  disabled={isAssistantDisabled || isNewChatTransitioning}
                />
              ))}
            </Stack>
          </Box>
          <Divider />
        </>
      ) : null}

      <Box sx={{ p: 1.25 }}>
        <TextField
          multiline
          minRows={2}
          maxRows={5}
          fullWidth
          placeholder="Ask an internal admin question..."
          value={messageDraft}
          onChange={(event) => setMessageDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              if (isNewChatTransitioning) {
                return;
              }
              void handleSendMessage();
            }
          }}
        />

        <Box
          sx={{
            mt: 1,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={() => {
              void handleSendMessage();
            }}
            disabled={
              isAssistantDisabled ||
              sendMessageState.isLoading ||
              isNewChatTransitioning ||
              messageDraft.trim().length === 0
            }
          >
            Send
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AdminAiAssistantWidget;
